/**
 * Alert API Service
 * Provides methods for alert acknowledgment, resolution, and history
 */

import { post, get } from './api'
import { ENDPOINTS } from './endpoints'

// ============================================================================
// Types
// ============================================================================

export interface AlertHistoryEntry {
  id: string
  alert_id: string
  action: 'created' | 'acknowledged' | 'resolved' | 'escalated' | 'updated'
  user: string
  timestamp: string
  notes?: string
  details?: Record<string, unknown>
}

// ============================================================================
// Alert API
// ============================================================================

export const alertApi = {
  /** Acknowledge an alert (POST version for frontend compatibility) */
  async acknowledgeAlert(alertId: string, user: string): Promise<{ status: string }> {
    return post<{ status: string }>(
      `${ENDPOINTS.ALERTS_ACKNOWLEDGE(alertId)}`,
      null,
      { params: { user } }
    )
  },

  /** Resolve an alert with optional resolution notes */
  async resolveAlert(
    alertId: string,
    user: string,
    resolution?: string,
  ): Promise<{ status: string }> {
    return post<{ status: string }>(
      `${ENDPOINTS.ALERTS_RESOLVE(alertId)}`,
      null,
      { params: { user, resolution: resolution || 'resolved' } }
    )
  },

  /** Get alert history for an asset */
  async getAlertHistory(assetId: string): Promise<AlertHistoryEntry[]> {
    return get<AlertHistoryEntry[]>(ENDPOINTS.ALERTS_HISTORY(assetId))
  },

  /** Get alert stats summary */
  async getStats(): Promise<Record<string, unknown>> {
    return get(ENDPOINTS.ALERTS_STATS)
  },

  /** Generate alert from prediction (auto-threshold with severity mapping) */
  async generateFromPrediction(params: {
    asset_id: string
    anomaly_score: number
    category?: string
    threshold?: number
  }): Promise<{ status: string; alert_id?: string; severity?: string; anomaly_score?: number; reason?: string }> {
    return post(ENDPOINTS.ALERTS_FROM_PREDICTION, null, {
      params: {
        asset_id: params.asset_id,
        anomaly_score: params.anomaly_score,
        category: params.category,
        threshold: params.threshold ?? 0.5,
      },
    })
  },
}
